import React from 'react'

import { cn } from '@harvey-frontend/utils/utils'
import { Check, Undo2 } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@harvey-frontend/components/ui/button'
import { Spinner } from '@harvey-frontend/components/ui/spinner'

import { useFetchPlaybookRuleEdits } from '../../hooks/useFetchPlaybookRuleEdits'
import { SuggestionType } from '../../hooks/usePlaybookRuleSuggestions'
import useAppStore from '../../stores/store'
import { PlaybookEditSuggestion } from '../../utils/playbook-utils'

interface SuggestionCardProps {
  ruleName: string
  suggestion?: PlaybookEditSuggestion
  title: SuggestionType
  range: Word.Range | null
  referenceId: string
  originalText: string | null
  isApplied: boolean
}

export const SuggestionCard = ({
  ruleName,
  suggestion,
  title,
  range,
  referenceId,
  originalText,
  isApplied,
}: SuggestionCardProps) => {
  const {
    mutateAsync: fetchAndApplyEdits,
    isPending: isApplying,
    reset,
  } = useFetchPlaybookRuleEdits()

  const setSuggestionApplied = useAppStore(
    useShallow((state) => state.markSuggestionApplied)
  )

  if (!suggestion || !range) return null

  const handleApplyEdit = async (
    useOriginalText: boolean,
    suggestionToApply: PlaybookEditSuggestion
  ) => {
    reset()
    await fetchAndApplyEdits({
      suggestionId: suggestionToApply.suggestion.id,
      suggestionText: useOriginalText
        ? originalText!
        : suggestion.suggestion.suggestion,
      clauseText: suggestionToApply.clauseText,
      range,
      referenceId,
      ruleName,
      useOriginalText,
    })
    if (useOriginalText) {
      setSuggestionApplied(ruleName, referenceId, null)
    } else {
      setSuggestionApplied(
        ruleName,
        referenceId,
        suggestionToApply.suggestion.id
      )
    }
  }

  return (
    <div className="flex flex-col">
      {suggestion && (
        <Button
          variant="ghost"
          onClick={() =>
            !isApplying && !isApplied && handleApplyEdit(isApplied, suggestion)
          }
          className={cn(
            'flex h-auto w-full flex-col items-start gap-2 border border-transparent p-2 transition hover:border hover:border-input hover:bg-transparent',
            {
              'cursor-default': isApplying || isApplied,
              'border border-input bg-accent hover:bg-accent': isApplied,
            }
          )}
        >
          <div className="flex flex-col gap-1 text-left">
            <span className="text-xs">{title}</span>
            <span className="text-xs text-muted">
              {suggestion.suggestion.suggestion}
            </span>
          </div>
          {isApplying && (
            <div className="flex shrink-0 items-center py-1">
              <Spinner size="xxs" />
              <span className="text-xs text-inactive">Applying...</span>
            </div>
          )}
          {!isApplying && isApplied && (
            <div className="flex w-full shrink-0 items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Check className="size-3 shrink-0 text-inactive" />
                <span className="text-xs text-inactive">Applied</span>
              </div>
              <Button
                variant="secondary"
                size="xsIcon"
                className="w-auto p-1"
                onClick={() =>
                  handleApplyEdit(/*useOriginalText*/ true, suggestion)
                }
              >
                <div className="flex items-center gap-1 transition">
                  <Undo2 className="size-3 shrink-0" />
                  <span className="text-xs">Undo</span>
                </div>
              </Button>
            </div>
          )}
        </Button>
      )}
    </div>
  )
}
